import { ACTIVE_DIRECTORY_INTERVALS } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/settings-integrations-active-directory.constants';
import {
    ActiveDirectoryDetailsFormState,
    ConnectionStatus,
    ConnectionStatusCodes,
    TestConnectionState,
    TestConnectionStatus,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';

export type ConnectionFormFieldName = 'name' | 'url' | 'group' | 'interval' | 'username' | 'userGroup' | 'password';

export type ConnectionFormFieldErrors = Partial<Record<ConnectionFormFieldName, string>>;

export const CONNECTION_FORM_FIELDS: ConnectionFormFieldName[] = [
    'name',
    'url',
    'group',
    'interval',
    'username',
    'userGroup',
    'password',
];

export const isValidIntervalValue = (value: string): boolean =>
    Boolean(value) && Object.values(ACTIVE_DIRECTORY_INTERVALS).includes(value);

export const isValidConnectionFormState = (
    formState: ActiveDirectoryDetailsFormState,
    hasSavedPassword?: boolean
): boolean =>
    Boolean(
        formState.name &&
        formState.url &&
        formState.group &&
        isValidIntervalValue(formState.interval) &&
        formState.username &&
        formState.userGroup &&
        (formState.password || hasSavedPassword)
    );

export const getRequiredFieldError = (
    field: ConnectionFormFieldName,
    value: string,
    hasSavedPassword?: boolean
): boolean => {
    if (field === 'password') {
        return !value && !hasSavedPassword;
    }
    if (field === 'interval') {
        return !isValidIntervalValue(value);
    }
    return !value;
};

interface ConnectionStatusParams {
    isDirty: boolean;
    testConnectionState: TestConnectionState;
    activeDirectoryStatus?: ConnectionStatus;
}

export const getConnectionStatus = ({
    isDirty,
    testConnectionState,
    activeDirectoryStatus,
}: ConnectionStatusParams): ConnectionStatus => {
    if (testConnectionState.status === TestConnectionStatus.SUCCESS) {
        return ConnectionStatusCodes.LAST_CONNECTION_SUCCESS;
    }
    if (testConnectionState.status === TestConnectionStatus.TESTING) {
        return ConnectionStatusCodes.TESTING_CONNECTION;
    }
    if (testConnectionState.status === TestConnectionStatus.ERROR) {
        return ConnectionStatusCodes.LAST_CONNECTION_FAILED;
    }
    if (isDirty) {
        return ConnectionStatusCodes.NOT_CONFIGURED;
    }
    return activeDirectoryStatus || ConnectionStatusCodes.NOT_CONFIGURED;
};

interface ConnectionErrorMessageParams {
    isEditMode: boolean;
    isDirty: boolean;
    testConnectionState: TestConnectionState;
    errorConnectionDetail?: string;
}

export const getConnectionErrorMessage = ({
    isEditMode,
    isDirty,
    testConnectionState,
    errorConnectionDetail,
}: ConnectionErrorMessageParams): string => {
    if (!isEditMode || isDirty) {
        return testConnectionState.status === TestConnectionStatus.ERROR ? testConnectionState.message : '';
    }
    return errorConnectionDetail || '';
};
